<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { fieldErrors } from '@/api/client';
import BaseModal from '@/components/BaseModal.vue';
import { useAuthStore } from '@/stores/auth';
import { useUiStore } from '@/stores/ui';

const emit = defineEmits(['close']);
const auth = useAuthStore();
const ui = useUiStore();
const { t } = useI18n();

const form = ref({ username: '', email: '', password: '', passwordRepeat: '' });
const errors = ref({});
const busy = ref(false);

async function submit() {
  if (busy.value) return;
  busy.value = true;
  errors.value = {};
  try {
    await auth.signUp(form.value);
    emit('close', true);
  } catch (error) {
    // Il backend risponde 401 quando ALLOW_NEW_REGISTRATIONS è false: non è
    // un errore di compilazione, è il proprietario che ha chiuso le iscrizioni.
    if (error?.response?.status === 401) {
      ui.notifyError(t('signUpClosed'));
      emit('close', false);
      return;
    }
    errors.value = fieldErrors(error);
  } finally {
    busy.value = false;
  }
}
</script>

<template>
  <BaseModal :title="$t('signUpTitle')" @close="emit('close')">
    <form id="signup-form" @submit.prevent="submit">
      <label class="field">
        <span class="field__label">{{ $t('usernameLabel') }}</span>
        <input
          v-model="form.username"
          class="input"
          :class="{ 'input--invalid': errors.username }"
          type="text"
          autocomplete="username"
          autofocus
          required
          :placeholder="$t('usernamePlaceholder')"
        />
        <span v-if="errors.username" class="field__error">{{ errors.username }}</span>
      </label>

      <label class="field">
        <span class="field__label">{{ $t('emailLabel') }}</span>
        <input
          v-model="form.email"
          class="input"
          :class="{ 'input--invalid': errors.email }"
          type="email"
          autocomplete="email"
          required
          :placeholder="$t('emailPlaceholder')"
        />
        <span v-if="errors.email" class="field__error">{{ errors.email }}</span>
      </label>

      <label class="field">
        <span class="field__label">{{ $t('passwordLabel') }}</span>
        <input
          v-model="form.password"
          class="input"
          :class="{ 'input--invalid': errors.password }"
          type="password"
          autocomplete="new-password"
          required
          :placeholder="$t('passwordSignUpPlaceholder')"
        />
        <span v-if="errors.password" class="field__error">{{ errors.password }}</span>
      </label>

      <label class="field">
        <span class="field__label">{{ $t('repeatPasswordLabel') }}</span>
        <input
          v-model="form.passwordRepeat"
          class="input"
          :class="{ 'input--invalid': errors.password_repeat }"
          type="password"
          autocomplete="new-password"
          required
          :placeholder="$t('repeatPasswordInputPlaceholder')"
        />
        <span v-if="errors.password_repeat" class="field__error">{{ errors.password_repeat }}</span>
      </label>
    </form>

    <template #footer>
      <button class="btn" type="button" @click="emit('close')">{{ $t('cancelButton') }}</button>
      <button class="btn btn--primary" type="submit" form="signup-form" :disabled="busy">
        {{ $t('registerButton') }}
      </button>
    </template>
  </BaseModal>
</template>
